/**
 * Error Handler Module
 * Centralizes error handling, logging, and user notifications
 */

/**
 * Initialize global error handlers
 */
export function initializeErrorHandling() {
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

/**
 * Handle global JavaScript errors
 * @param {ErrorEvent} e - Error event
 */
function handleGlobalError(e) {
    console.error('Application Error:', e.error);

    // Log error to analytics (in production)
    logErrorToAnalytics(e.error);

    // Show user-friendly error message for critical errors
    if (isCriticalError(e.error)) {
        showErrorMessage('Something went wrong. Please refresh the page.');
    }
}

/**
 * Handle unhandled promise rejections
 * @param {PromiseRejectionEvent} e - Promise rejection event
 */
function handleUnhandledRejection(e) {
    console.error('Unhandled Promise Rejection:', e.reason);

    // Log to analytics
    logErrorToAnalytics(e.reason);
}

/**
 * Check if error is critical
 * @param {Error} error - Error object
 * @returns {boolean} True if critical
 */
function isCriticalError(error) {
    if (!error || !error.stack) return false;
    return error.stack.includes('critical');
}

/**
 * Log error to analytics service (Google Analytics)
 * @param {Error|string} error - Error to log
 */
function logErrorToAnalytics(error) {
    if (typeof gtag !== 'undefined') {
        gtag('event', 'exception', {
            description: error.toString(),
            fatal: false
        });
    }
}

/**
 * Show error message notification to user
 * @param {string} message - Error message to display
 * @param {number} duration - Display duration in ms (default: 5000)
 */
export function showErrorMessage(message, duration = 5000) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-notification';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.setAttribute('aria-live', 'assertive');

    errorDiv.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #ff4444;
        color: white;
        padding: 1rem 2rem;
        border-radius: 10px;
        box-shadow: 0 5px 20px rgba(255, 68, 68, 0.4);
        z-index: 10001;
        animation: slideInDown 0.3s ease-out;
        max-width: 90%;
        text-align: center;
    `;
    errorDiv.textContent = message;

    document.body.appendChild(errorDiv);

    // Auto-remove after duration
    setTimeout(() => {
        errorDiv.style.animation = 'slideOutUp 0.3s ease-in';
        setTimeout(() => errorDiv.remove(), 300);
    }, duration);
}

/**
 * Show success message notification
 * @param {string} message - Success message to display
 * @param {number} duration - Display duration in ms (default: 3000)
 */
export function showSuccessMessage(message, duration = 3000) {
    const successDiv = document.createElement('div');
    successDiv.className = 'success-notification';
    successDiv.setAttribute('role', 'status');
    successDiv.setAttribute('aria-live', 'polite');

    successDiv.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #28a745;
        color: white;
        padding: 1rem 2rem;
        border-radius: 10px;
        box-shadow: 0 5px 20px rgba(40, 167, 69, 0.4);
        z-index: 10001;
        animation: slideInDown 0.3s ease-out;
        max-width: 90%;
        text-align: center;
    `;
    successDiv.textContent = message;

    document.body.appendChild(successDiv);

    setTimeout(() => {
        successDiv.style.animation = 'slideOutUp 0.3s ease-in';
        setTimeout(() => successDiv.remove(), 300);
    }, duration);
}

/**
 * Show warning message notification
 * @param {string} message - Warning message to display
 * @param {number} duration - Display duration in ms (default: 4000)
 */
export function showWarningMessage(message, duration = 4000) {
    const warningDiv = document.createElement('div');
    warningDiv.className = 'warning-notification';
    warningDiv.setAttribute('role', 'status');
    warningDiv.setAttribute('aria-live', 'polite');

    warningDiv.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #ffc107;
        color: #333;
        padding: 1rem 2rem;
        border-radius: 10px;
        box-shadow: 0 5px 20px rgba(255, 193, 7, 0.4);
        z-index: 10001;
        animation: slideInDown 0.3s ease-out;
        max-width: 90%;
        text-align: center;
    `;
    warningDiv.textContent = message;

    document.body.appendChild(warningDiv);

    setTimeout(() => {
        warningDiv.style.animation = 'slideOutUp 0.3s ease-in';
        setTimeout(() => warningDiv.remove(), 300);
    }, duration);
}

/**
 * Cleanup error handlers
 */
export function cleanupErrorHandling() {
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
}
